import type { RunReport } from "./types";

type Check = RunReport["checks"][number];
type AssessmentItem = Check["assessment"]["items"][number];
type StateSource = Check["stateSources"][number];

/** A state's own result: its automated assessment status, or not screened
 * because its list was not current when the check ran. It never reaches
 * "in-progress" or "incomplete"; those describe the shared check before it
 * collects. */
export const StateResult = {
  AttentionNeeded: "attention-needed",
  NotScreened: "not-screened",
  OlderList: "older-list",
  Limited: "limited",
  Clear: "clear",
} as const;
export type StateResult = (typeof StateResult)[keyof typeof StateResult];

// Assessment statuses a state's result keeps; any other reads as limited.
const STATE_RESULT_BY_STATUS: Partial<Record<string, StateResult>> = {
  "attention-needed": StateResult.AttentionNeeded,
  limited: StateResult.Limited,
  clear: StateResult.Clear,
};

type ResultRow = {
  source: Pick<StateSource, "unavailable" | "stale">;
  item?: Pick<AssessmentItem, "status">;
};

function stateResult(row: ResultRow): StateResult {
  if (row.source.unavailable) {
    return StateResult.NotScreened;
  }
  const result =
    STATE_RESULT_BY_STATUS[row.item?.status ?? ""] ?? StateResult.Limited;
  // Possible exclusions always win; a stale list only matters when the old
  // copy found nothing, because "nothing found" may be out of date.
  if (row.source.stale && result !== StateResult.AttentionNeeded) {
    return StateResult.OlderList;
  }
  return result;
}

/** Buckets states by their own result so the report can lead with what needs
 * action. The one review covering every state is the group's badge, so a
 * review need is never multiplied across states. */
export function groupStatesByResult<Row extends ResultRow>(rows: Row[]) {
  const groups: Record<StateResult, Row[]> = {
    [StateResult.AttentionNeeded]: [],
    [StateResult.NotScreened]: [],
    [StateResult.OlderList]: [],
    [StateResult.Limited]: [],
    [StateResult.Clear]: [],
  };
  rows.forEach((row) => {
    groups[stateResult(row)].push(row);
  });
  return groups;
}
